import { LoggerProvider } from '../../logger';
import {
    IdsOfElements,
    LoadingSpeed,
    RESIZE_DEFAULT_TIMEOUT_BASE,
    ViewSource,
} from '../../../models/enums/general-enums';

import Container from 'typedi';
import { IntersectionObserverManager } from './IntersectionObserverManager';
import { ResizeObserverManager } from './ResizeObserverManager';
import { Utils } from '../../utils';
import DeferredObject from '../../../../Modules/Utils/DeferredObject';
import { WindowToken } from '../../../injection-tokens';
import { BrandUtils } from '../../../../Modules/Utils/BrandUtils';
import { ICMSContentLoader } from '../models/ICMSContentLoader';
import { ILogger } from '@sparkware/uc-sdk-core';

const WAITING_TIMES = {
    SLOW: 2000,
    MEDIUM: 1000,
    FAST: 500,
    SUPER_FAST: 250,
};

const DEFAULT_STATE = {
    CMSContentResize: false,
    CMSContentIntersection: false,
    ClientResize: false,
};

export class CMSContentLoader implements ICMSContentLoader {
    private _waitTime: number;
    private _readyState: {
        CMSContentIntersection: boolean;
        CMSContentResize: boolean;
        ClientResize?: boolean;
    };
    private _cmsContentResizeObserver: ResizeObserverManager;

    private _clientResizeObserver: ResizeObserverManager;
    private _cmsContentIntersectionObserver: IntersectionObserverManager;
    private _clientInitalizedDO: DeferredObject<boolean> = new DeferredObject<boolean>({
        storeResolved: true,
    });
    private readonly _logger: ILogger;
    private readonly _utils: Utils;
    private readonly _window: Window;
    private readonly _cleanupObject = { cleanClient: null, cleanContent: null };

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('CMSContentLoader');
        this._utils = Container.get(Utils);
        this._window = Container.get(WindowToken);
        this._readyState = DEFAULT_STATE;

        this._cmsContentResizeObserver = new ResizeObserverManager(
            'CMSContentLoader: CMSContentResizeObserverManager',
        );
        this._clientResizeObserver = new ResizeObserverManager(
            'CMSContentLoader: ClientResizeObserverManager',
        );
        this._cmsContentIntersectionObserver = new IntersectionObserverManager(
            'CMSContentLoader: CMSContentIntersectionObserverManager',
        );

        this._getWaitingTime().then((wt) => (this._waitTime = wt));
    }

    public ResolveWhenReady = async (nextView: ViewSource): Promise<void> => {
        if (this._utils.isPrerenderRequest()) {
            return Promise.resolve();
        }
        return new Promise(async (resolve) => {
            this.Disconnect();
            switch (nextView) {
                case ViewSource.Content: {
                    if (BrandUtils.isSpectate()) {
                        this._clientInitalizedDO = new DeferredObject({ storeResolved: true });
                    }

                    this._readyState = {
                        CMSContentIntersection: true,
                        CMSContentResize: false,
                    };

                    this._observeCmsContentResizeObserver((newState) => {
                        this._readyState.CMSContentResize = newState;

                        this._logger.debug(
                            `ResolveWhenReady: CMSContentResizeObserverManager: currentState: ${JSON.stringify(
                                this._readyState,
                            )}`,
                        );
                        if (this._checkIfReadyToExecute()) {
                            this.Disconnect();
                            resolve();
                        }
                    });
                    break;
                }
                case ViewSource.Sport: {
                    this._readyState = {
                        CMSContentIntersection: false,
                        CMSContentResize: false,
                        ClientResize: false,
                    };

                    const clientElement = document.getElementById(
                        IdsOfElements.UcSpectateSportClientView,
                    );

                    this._cleanupObject.cleanContent = this._observeCmsContentResizeObserver(
                        (newState) => {
                            this._readyState.CMSContentResize = newState;

                            this._logger.debug(
                                `ResolveWhenReady: CMSContentResizeObserverManager: currentState: ${JSON.stringify(
                                    this._readyState,
                                )}`,
                            );

                            if (this._checkIfReadyToExecute()) {
                                this.Disconnect();
                                resolve();
                            }
                        },
                    );

                    this._observeCmsContentIntersectionObserver((newState) => {
                        this._readyState.CMSContentIntersection = newState;

                        if (
                            !this._readyState.ClientResize &&
                            newState &&
                            clientElement.offsetHeight > this._window.innerHeight
                        ) {
                            this._clientResizeObserver.Disconnect();
                            this._readyState.ClientResize = true;
                        }
                        this._logger.debug(
                            `ResolveWhenReady: CMSContentIntersectionObserverManager: currentState: ${JSON.stringify(
                                this._readyState,
                            )}`,
                        );

                        if (this._checkIfReadyToExecute()) {
                            resolve();
                        }
                    });

                    const clientInitSucceeded = await this._clientInitalizedDO.promise;

                    if (clientInitSucceeded) {
                        this._cleanupObject.cleanClient = this._observeClientResizeObserver(
                            (newState) => {
                                const clientOutOfViewPortClientElement =
                                    clientElement.offsetHeight > this._window.innerHeight;

                                if (clientOutOfViewPortClientElement) {
                                    this._logger.debug(
                                        `ResolveWhenReady: ClientResizeObserverManager: clientElement is bigger than the viewport size (${clientElement.offsetHeight} > ${this._window.innerHeight})`,
                                    );
                                }

                                this._readyState.ClientResize =
                                    newState &&
                                    clientElement.offsetHeight > 0.85 * this._window.innerHeight;

                                this._logger.debug(
                                    `ResolveWhenReady: ClientResizeObserverManager: currentState: ${JSON.stringify(
                                        this._readyState,
                                    )}`,
                                );

                                if (this._checkIfReadyToExecute()) {
                                    this.Disconnect();
                                    resolve();
                                }

                                if (
                                    newState &&
                                    !this._readyState.ClientResize &&
                                    clientElement.offsetHeight > 20 &&
                                    this._readyState.CMSContentIntersection
                                ) {
                                    const timeout = 1 * RESIZE_DEFAULT_TIMEOUT_BASE;
                                    this._logger.debug(
                                        `ResolveWhenReady: ClientResizeObserverManager: waiting additional ${timeout} ms before setting the state to resolved.`,
                                    );

                                    setTimeout(() => {
                                        this._readyState.ClientResize = newState;
                                        if (this._checkIfReadyToExecute()) {
                                            this.Disconnect();
                                            resolve();
                                        }

                                        this._logger.debug(
                                            `ResolveWhenReady: ClientResizeObserverManager: currentState [on timeout]: ${JSON.stringify(
                                                this._readyState,
                                            )}`,
                                        );
                                    }, timeout);
                                }
                            },
                        );
                    } else {
                        this._readyState.ClientResize = true;
                    }

                    break;
                }
            }
        });
    };

    public Disconnect = () => {
        this._logger.debug('_disconnect: Disconnecting observers...');
        this._cmsContentResizeObserver.Disconnect();
        this._cmsContentIntersectionObserver.Disconnect();
        this._clientResizeObserver.Disconnect();

        if (typeof this._cleanupObject.cleanClient === 'function') {
            this._cleanupObject.cleanClient();
            this._cleanupObject.cleanClient = null;
        }

        if (typeof this._cleanupObject.cleanContent === 'function') {
            this._cleanupObject.cleanContent();
            this._cleanupObject.cleanContent = null;
        }
    };

    public OnClientInit = (successed: boolean) => {
        this._clientInitalizedDO.resolve(successed);
    };

    private _checkIfReadyToExecute = () => {
        const readyToExecute = Object.values(this._readyState).every((value) => !!value);
        if (readyToExecute) {
            this.Disconnect();
        }

        return readyToExecute;
    };

    private _observeCmsContentResizeObserver = (stateCallback: (state) => void) => {
        this._logger.debug('_observeCmsContentResizeObserver: Observing...');
        const { clean, callback } = this._createResizeObserverCallback('CMSContent', stateCallback);
        this._cmsContentResizeObserver.ObserveElement(
            document.getElementById(IdsOfElements.UcCMSContainer),
            callback,
        );

        return clean;
    };

    private _observeClientResizeObserver = (stateCallback: (state) => void) => {
        this._logger.debug('_observeClientResizeObserver: Observing...');
        const { clean, callback } = this._createResizeObserverCallback(
            'Client',
            stateCallback,
            2.5 * RESIZE_DEFAULT_TIMEOUT_BASE,
        );
        this._clientResizeObserver.ObserveElement(
            document.getElementById(IdsOfElements.UcSpectateSportClientView),
            callback,
        );

        return clean;
    };

    private _observeCmsContentIntersectionObserver = (stateCallback: (state) => void) => {
        this._logger.debug('_observeCmsContentIntersectionObserver: Observing...');
        const callback = this._createIntersectionObserverCallback(stateCallback);
        this._cmsContentIntersectionObserver.ObserveElement(
            document.getElementById(IdsOfElements.UcCMSContainer),
            callback,
        );
    };

    private _createIntersectionObserverCallback = (callback: (state: boolean) => void) => {
        let loaded = false,
            startTime = Date.now();

        return (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            callback(loaded);

            const isInIntersection = entries.some((entry) => entry.isIntersecting);

            this._logger.debug(
                '_createIntersectionObserverCallback: IntersectionObserver detected size changes on observed elements...',
            );

            const elapsedTime = Date.now() - startTime;
            this._logger.debug(
                `_createIntersectionObserverCallback: Time passed since last observer update: ${elapsedTime}ms`,
            );

            startTime = Date.now();
            this._logger.debug(`_createIntersectionObserverCallback: Timer was fired...`);
            callback(isInIntersection);
        };
    };

    private _createResizeObserverCallback = (
        component: string,
        callback: (state: boolean) => void,
        defaultWaitTime: number = 3 * RESIZE_DEFAULT_TIMEOUT_BASE,
    ) => {
        let timeout = null,
            loaded = false,
            attemptNumber = 0,
            startTime = Date.now(),
            waitTime = this._waitTime || defaultWaitTime;

        timeout = setTimeout(() => {
            this._logger.debug(
                `_createResizeObserverCallback: ${component}: Timer was fired... no updates for ${waitTime} ms.`,
            );

            loaded = true;
            callback(loaded);
        }, waitTime);

        return {
            clean: () => {
                this._logger.debug(
                    `_createResizeObserverCallback: ${component}: attempt to cleanup the timeout on resize observer...`,
                );
                if (timeout) {
                    this._logger.debug(
                        `_createResizeObserverCallback: ${component}: cleaned up timeout with id ${timeout}`,
                    );
                    clearTimeout(timeout);
                }
            },
            callback: async (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
                loaded = false;
                callback(loaded);

                this._logger.debug(
                    `_createResizeObserverCallback: ${component}: ResizeObserver detected size changes on observed elements...`,
                );
                if (timeout) {
                    clearTimeout(timeout);
                    this._logger.debug(
                        `_createResizeObserverCallback: ${component}: Clearing existing timers...`,
                    );
                    const elapsedTime = Date.now() - startTime;
                    this._logger.debug(
                        `_createResizeObserverCallback: ${component}: Time passed since last observer update: ${elapsedTime}ms`,
                    );
                }

                attemptNumber++;
                this._logger.debug(
                    `_createResizeObserverCallback: ${component}: [Attempt#${attemptNumber}]:  Waiting ${waitTime} more milliseconds between content updates in order to execute registered callbacks...`,
                );

                startTime = Date.now();
                waitTime = this._waitTime || waitTime;

                timeout = setTimeout(() => {
                    this._logger.debug(
                        `_createResizeObserverCallback: ${component}: Timer was fired...`,
                    );
                    loaded = true;
                    callback(loaded);
                }, waitTime);
            },
        };
    };

    private _getWaitingTime = async () => {
        const loadingSpeed = await this._utils.getLoadingSpeed();

        switch (loadingSpeed) {
            case LoadingSpeed.SUPER_FAST: {
                return WAITING_TIMES.SUPER_FAST;
            }
            case LoadingSpeed.FAST: {
                return WAITING_TIMES.FAST;
            }
            case LoadingSpeed.SLOW: {
                return WAITING_TIMES.SLOW;
            }
            case LoadingSpeed.MEDIUM:
            default: {
                return WAITING_TIMES.MEDIUM;
            }
        }
    };
}
